export interface DashboardResource<T> {
  data: T | null;
  loading: boolean;
  ready: boolean;
  stale: boolean;
  error: string | null;
  lastUpdated: string | null;
}

export const DEFAULT_STALE_AFTER_MS = 90_000;

export function createDashboardResource<T>(): DashboardResource<T> {
  return {
    data: null,
    loading: false,
    ready: false,
    stale: false,
    error: null,
    lastUpdated: null,
  };
}

function extractMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'unknown error';
}

export function markResourceLoading<T>(resource: DashboardResource<T>): DashboardResource<T> {
  return {
    ...resource,
    loading: true,
    error: null,
  };
}

export function markResourceSuccess<T>(
  _resource: DashboardResource<T>,
  data: T,
  lastUpdated: string,
): DashboardResource<T> {
  return {
    data,
    loading: false,
    ready: true,
    stale: false,
    error: null,
    lastUpdated,
  };
}

export function markResourceError<T>(
  resource: DashboardResource<T>,
  error: unknown,
): DashboardResource<T> {
  return {
    ...resource,
    loading: false,
    ready: resource.ready,
    stale: !!resource.ready,
    error: extractMessage(error),
  };
}

export function updateResourceStaleness<T>(
  resource: DashboardResource<T>,
  staleAfterMs = DEFAULT_STALE_AFTER_MS,
): DashboardResource<T> {
  if (!resource.ready || !resource.lastUpdated) {
    return resource;
  }

  const age = Date.now() - Date.parse(resource.lastUpdated);
  if (!Number.isFinite(age)) {
    return { ...resource, stale: false };
  }

  const stale = age > staleAfterMs;
  if (stale === resource.stale) {
    return resource;
  }

  return {
    ...resource,
    stale,
  };
}
